/**
 * UpcomingDeadlines — next-30+ days GxP deadline strip.
 *
 * Pulls site licenses, equipment requalification + calibration, supplier
 * requals, and planned audits. Sorted by days-until-due. Color-coded by
 * urgency (overdue / high / medium / low).
 */

import { useGxpDeadlines, type GxpDeadline } from '../../api/orgHooks';
import styles from './HomeDashboardCards.module.css';

const KIND_LABEL: Record<GxpDeadline['kind'], string> = {
  SITE_LICENSE: 'License',
  EQUIPMENT_REQUAL: 'Equip. requal',
  EQUIPMENT_CAL: 'Calibration',
  SUPPLIER_REQUAL: 'Supp. requal',
  AUDIT: 'Audit',
};
const KIND_ICON: Record<GxpDeadline['kind'], string> = {
  SITE_LICENSE: '📜',
  EQUIPMENT_REQUAL: '🔧',
  EQUIPMENT_CAL: '⚖️',
  SUPPLIER_REQUAL: '🤝',
  AUDIT: '🕵️',
};

function fmtDate(iso: string) {
  try {
    return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
  } catch {
    return iso;
  }
}

export function UpcomingDeadlines() {
  const q = useGxpDeadlines();
  const items = q.data?.items ?? [];
  if (!q.isLoading && items.length === 0) return null;

  return (
    <section className={styles.deadlinesCard} aria-label="Upcoming GxP deadlines">
      <header className={styles.cardHead}>
        <h2 className={styles.cardTitle}>Upcoming GxP deadlines</h2>
        <span className={styles.cardSub}>Next 30–90 days</span>
      </header>

      <div className={styles.deadlinesStrip}>
        {items.slice(0, 8).map((d) => (
          <div
            key={`${d.kind}-${d.ref}`}
            className={`${styles.deadlineItem} ${styles[`urgency_${d.urgency}`]}`}
          >
            <div className={styles.deadlineHead}>
              <span aria-hidden>{KIND_ICON[d.kind]}</span>
              <span className={styles.deadlineKind}>{KIND_LABEL[d.kind]}</span>
            </div>
            <div className={styles.deadlineRef}>{d.ref}</div>
            <div className={styles.deadlineTitle}>{d.title}</div>
            <div className={styles.deadlineDue}>
              <span className={styles.deadlineDate}>{fmtDate(d.due_date)}</span>
              <span className={styles.deadlineDays}>
                {d.days_until_due < 0
                  ? `${Math.abs(d.days_until_due)}d overdue`
                  : d.days_until_due === 0
                  ? 'today'
                  : `in ${d.days_until_due}d`}
              </span>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
